import React, { useEffect, useState } from "react";
import { BASE_URL } from "../constants/Constants";
import axios from "axios";
import { Table, Input, Button, Modal, message, Space, Tooltip } from "antd";
import { EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import AddProductForm from "../components/AddProductForm";
import UpdateProductForm from "../components/UpdateProductForm"; // IMPORT UPDATE FORM

const { confirm } = Modal;

const ProductDetails = () => {
  const userId = localStorage.getItem("userId");
  const [product, setProduct] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  
  // Modal States
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false); // FOR EDIT MODAL
  const [selectedProduct, setSelectedProduct] = useState(null); // STORE PRODUCT TO EDIT
  const [updatedBoolean, setUpdatedBoolean] = useState(false); // TRIGGER REFRESH

  // --- ADD PRODUCT HANDLERS ---
  const handleAddProductClick = () => {
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const handleSuccess = () => {
    fetchData(); 
    handleModalClose();
  };

  // --- EDIT PRODUCT HANDLERS ---
  const handleEdit = (record) => {
    setSelectedProduct(record);
    setIsUpdateModalVisible(true);
  };

  const handleUpdateModalClose = () => {
    setIsUpdateModalVisible(false);
    setSelectedProduct(null);
  };

  // --- DELETE PRODUCT HANDLERS ---
  const showDeleteConfirm = (productId) => {
    confirm({
      title: 'Are you sure delete this product?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        handleDelete(productId);
      },
    });
  };

  const handleDelete = async (productId) => {
    try {
      await axios.delete(`${BASE_URL}/products/delete/${productId}`);
      message.success("Product deleted successfully");
      fetchData(); // Refresh list
    } catch (error) {
      console.error("Error deleting product:", error);
      message.error("Failed to delete product");
    }
  };

  // --- DATA FETCHING ---
  useEffect(() => {
    fetchData();
  }, [updatedBoolean]); // Re-fetch when update happens

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/products/allProductList`);
      console.log("API Response:", response.data);
      const items = response?.data || [];
      
      const combinedItems = items; 

      setProduct(combinedItems);
      setFilteredProducts(combinedItems);
      setLoading(false);
    } catch (error) {
      console.log("Error fetching products:", error);
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
    
    const filtered = product.filter(
      (p) =>
        p.productName?.toLowerCase().includes(value) ||
        String(p.productId).toLowerCase().includes(value)
    );
    setFilteredProducts(filtered);
  };

  // --- COLUMNS DEFINITION ---
  const columns = [
    {
      title: "ProductId",
      dataIndex: "productId",
      key: "productId",
    },
    {
      title: "Product Name",
      dataIndex: "productName",
      key: "productName",
    },
    {
      title: "Price",
      dataIndex: "productPrice", 
      key: "productPrice",
      sorter: (a, b) => a.productPrice - b.productPrice,
    },
    {
      title: "MRP",
      key: "productMRP",
      // Remove dataIndex or keep it, but 'render' takes priority for display
      render: (text, record) => {
        // Checks for productMRP, then mrp, then MRP, else returns 0
        const value = record.productMRP || record.mrp || record.MRP || 0;
        return `${value}`; // Optional: Adds currency symbol
      },
      // Update sorter to handle the fallback keys as well
      sorter: (a, b) => {
        const valA = a.productMRP || a.mrp || a.MRP || 0;
        const valB = b.productMRP || b.mrp || b.MRP || 0;
        return valA - valB;
      },
    },
    {
      title: "Stock",
      dataIndex: "productStock", 
      key: "productStock",
      sorter: (a, b) => a.productStock - b.productStock,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
    },
    // {
    //   title: "Priority", // SHOW PRIORITY IN TABLE
    //   dataIndex: "priority",
    //   key: "priority",
    //   sorter: (a, b) => (a.priority || 0) - (b.priority || 0),
    //   render: (priority) => (
    //     <span style={{ fontWeight: priority > 0 ? 'bold' : 'normal', color: priority > 0 ? '#1890ff' : 'inherit' }}>
    //       {priority || 0}
    //     </span>
    //   )
    // },
    // {
    //   title: "Active",
    //   dataIndex: "productHidden", 
    //   key: "productHidden",
    //   render: (isHidden) => (
    //     <span style={{ color: isHidden ? "red" : "green" }}>
    //       {isHidden ? "Inactive" : "Active"}
    //     </span>
    //   ),
    // },
    {
      title: "Action", // NEW ACTION COLUMN
      key: "action",
      render: (text, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <Button 
              type="primary" 
              shape="circle" 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)} 
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button 
              type="primary" 
              danger 
              shape="circle" 
              icon={<DeleteOutlined />} 
              onClick={() => showDeleteConfirm(record.productId)} 
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return userId ? (
    <div>
      <Input
        placeholder="Search Product by Name or ID"
        value={searchText}
        onChange={handleSearch}
        style={{ width: 300, marginBottom: 16, border: "1px solid #1890ff" }}
      />
      <Button
        type="primary"
        onClick={handleAddProductClick}
        style={{
          backgroundColor: "#0080FF",
          margin: "0 0 0 20px",
          marginBottom: 16,
        }}
      >
        Add Products
      </Button>
      
      {/* ADD FORM */}
      <AddProductForm 
        visible={isModalVisible} 
        onClose={handleModalClose} 
        onSuccess={handleSuccess} 
        product={product} 
      />

      {/* UPDATE FORM (The one with Priority Logic) */}
      <UpdateProductForm
        visible={isUpdateModalVisible}
        onClose={handleUpdateModalClose}
        product={selectedProduct}
        updatedBoolean={updatedBoolean}
        setUpdatedBoolean={setUpdatedBoolean}
      />
      
      <Table
        columns={columns}
        dataSource={filteredProducts}
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
        rowKey="productId"
      />
    </div>
  ) : (
    <div style={{ padding: "20px", background: "white", borderRadius: "10px" }}>
      Sign in to see Products Details
    </div>
  );
};

export default ProductDetails;